import { useEffect, useRef, useState } from "react";
import { m } from "framer-motion";

/**
 * GMB Widget review embed — lazy-loaded via IntersectionObserver.
 *
 * The iframe sits below the fold, so it is deferred until it is about to
 * scroll into view. A reserved min-height prevents layout shift when it mounts.
 */
const ReviewWidget = () => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || inView) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary-deep text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </m.h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Real reviews from homeowners and property owners across northern New Jersey.
          </p>
        </div>

        <m.div
          ref={wrapRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="review-widget-wrap max-w-5xl mx-auto"
          style={{ minHeight: 480 }}
        >
          {inView && (
            <iframe
              src="https://embed.gmbwidget.com/review-widget/w/rw_2931c167274fc6e29e62f2f2"
              title="Customer reviews"
              width="100%"
              height="480"
              style={{ border: "none", display: "block", maxWidth: "100%" }}
              loading="lazy"
            />
          )}
        </m.div>
      </div>
    </section>
  );
};

export default ReviewWidget;
